import { Fragment } from "react";
import { bowlerBalls, personName, phaseCell, played } from "./derive";
import type { Inn, PhaseCell } from "./derive";
import { Empty, useBoxWidth } from "./shared";
import type { Ctx } from "./shared";

const ORD = ["1st", "2nd", "3rd", "4th"];

/** One innings' runs-per-over bars (used for Test / multi-innings matches, where overlaying innings is unreadable). */
const ManhattanInnings = ({ inn, j, ctx }: { inn: Inn; j: number; ctx: Ctx }) => {
  const [boxRef, boxW] = useBoxWidth();
  const pl = 34, pr = 10, pt = 12, pb = 26;
  const overs = ctx.comms[j]?.overs ?? [];
  const slots = Math.max(Math.ceil(inn.legal / 6), overs.length, 1);
  // drawn at the real container width (true text size); long innings keep >= 7px per over and scroll sideways on phones
  const Wd = Math.max(boxW || 820, pl + pr + slots * 7), H = boxW && boxW < 500 ? 190 : 170;
  const maxRuns = Math.max(...overs.map((o) => o.runs), 1);
  const mx = Math.max(8, Math.ceil(maxRuns / 4) * 4);
  const slot = (Wd - pl - pr) / slots;
  const bw = Math.max(1.5, Math.min(slot * 0.72, 14));
  const Y = (v: number) => pt + (1 - v / mx) * (H - pt - pb);
  const grid: number[] = [];
  for (let v = 0; v <= mx; v += mx > 24 ? 8 : 4) grid.push(v);
  const labelEvery = slots > 100 ? 20 : slots > 50 ? 10 : slots > 30 ? 5 : slots > 20 ? 2 : 1;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, margin: "2px 0 4px" }}>{inn.team.name} · {ORD[j] ?? `${j + 1}th`} innings <span style={{ color: "#687080", fontWeight: 600 }}>{inn.runs}/{inn.wkts}</span></div>
      <div ref={boxRef} className="chscroll">
      <svg className="ch" style={{ width: Wd, height: H, maxWidth: "none" }} viewBox={`0 0 ${Wd} ${H}`} role="img" aria-label={`Runs per over, ${inn.team.name} ${ORD[j] ?? j + 1} innings`}>
        {grid.map((v) => (<g key={v}><line x1={pl} x2={Wd - pr} y1={Y(v)} y2={Y(v)} stroke="#EFF0F3" /><text x={pl - 8} y={Y(v) + 4} textAnchor="end" fontSize="11" fill="#9AA1AE">{v}</text></g>))}
        {Array.from({ length: slots }, (_, o) => {
          const cx = pl + slot * o + slot / 2;
          const ov = overs.find((x) => x.n === o + 1);
          return (
            <g key={o}>
              {(o + 1) % labelEvery === 0 || o === 0 ? <text x={cx} y={H - 8} textAnchor="middle" fontSize="10.5" fill="#687080">{o + 1}</text> : null}
              {ov && (<g><rect x={cx - bw / 2} y={Y(ov.runs)} width={bw} height={Math.max(0, H - pb - Y(ov.runs))} rx="1.5" fill={inn.team.color}><title>Over {o + 1}: {ov.runs} runs{ov.wkts ? `, ${ov.wkts} wkt` : ""}</title></rect>{ov.wkts > 0 && <circle cx={cx} cy={Y(ov.runs) - 6} r="3.4" fill="#fff" stroke={inn.team.color} strokeWidth="2" />}</g>)}
            </g>
          );
        })}
      </svg>
      </div>
      {boxW > 0 && Wd > boxW + 1 && <div className="chhint">Swipe sideways to see every over</div>}
    </div>
  );
};

const Manhattan = ({ inns, ctx }: { inns: Inn[]; ctx: Ctx }) =>
  ctx.multiDay ? <>{inns.map((inn, j) => (played(inn) ? <ManhattanInnings key={inn.id} inn={inn} j={j} ctx={ctx} /> : null))}</> : <ManhattanPaired inns={inns} ctx={ctx} />;

/** Limited-overs: both innings side by side per over. */
const ManhattanPaired = ({ inns, ctx }: { inns: Inn[]; ctx: Ctx }) => {
  const [boxRef, boxW] = useBoxWidth();
  const pl = 34, pr = 10, pt = 14, pb = 28;
  const slots = Math.max(...inns.map((i) => Math.max(i.maxOvers, Math.ceil(i.legal / 6))), 1);
  const Wd = Math.max(boxW || 820, pl + pr + slots * 13), H = boxW && boxW < 500 ? 220 : 240;
  const maxRuns = Math.max(...ctx.comms.flatMap((c) => c?.overs.map((o) => o.runs) ?? []), 1);
  const mx = Math.max(8, Math.ceil(maxRuns / 4) * 4);
  const slot = (Wd - pl - pr) / slots;
  const bw = Math.min(slot * 0.34, 14);
  const Y = (v: number) => pt + (1 - v / mx) * (H - pt - pb);
  const grid: number[] = [];
  for (let v = 0; v <= mx; v += 4) grid.push(v);
  const labelEvery = slots > 30 ? 5 : slots > 20 ? 2 : 1;
  return (
    <>
    <div ref={boxRef} className="chscroll">
    <svg className="ch" style={{ width: Wd, height: H, maxWidth: "none" }} viewBox={`0 0 ${Wd} ${H}`} role="img" aria-label="Runs per over">
      {grid.map((v) => (
        <g key={v}>
          <line x1={pl} x2={Wd - pr} y1={Y(v)} y2={Y(v)} stroke="#EFF0F3" />
          <text x={pl - 8} y={Y(v) + 4} textAnchor="end" fontSize="11" fill="#9AA1AE">{v}</text>
        </g>
      ))}
      {Array.from({ length: slots }, (_, o) => {
        const cx = pl + slot * o + slot / 2;
        return (
          <g key={o}>
            {(o + 1) % labelEvery === 0 || o === 0 ? <text x={cx} y={H - 9} textAnchor="middle" fontSize="10.5" fill="#687080">{o + 1}</text> : null}
            {inns.map((inn, j) => {
              const ov = ctx.comms[j]?.overs.find((x) => x.n === o + 1);
              if (!ov) return null;
              const x = cx + (j ? 2 : -bw - 2);
              return (
                <g key={inn.id}>
                  <rect x={x} y={Y(ov.runs)} width={bw} height={Math.max(0, H - pb - Y(ov.runs))} rx="2" fill={inn.team.color}>
                    <title>{inn.team.code} over {o + 1}: {ov.runs}</title>
                  </rect>
                  {ov.wkts > 0 && <circle cx={x + bw / 2} cy={Y(ov.runs) - 7} r="4" fill="#fff" stroke={inn.team.color} strokeWidth="2" />}
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
    </div>
    {boxW > 0 && Wd > boxW + 1 && <div className="chhint">Swipe sideways to see every over</div>}
    </>
  );
};

const AnalysisPane = ({ ctx }: { ctx: Ctx }) => {
  const { inns, comms } = ctx;
  const hasComm = comms.length > 0 && comms.every(Boolean);
  const phases = hasComm ? ctx.phases.map((p) => ({ label: p.label, a: p.from, b: p.to })) : [];
  const cells = (a: number, b: number): PhaseCell[] => inns.map((inn, k) => phaseCell(comms[k], inn, a, b));

  const cats = [
    { key: "dot", label: "Dots", color: "#D9DAE6" },
    { key: "run", label: "1s, 2s, 3s", color: "#A9A7D6" },
    { key: "four", label: "Fours", color: "#5B58B8" },
    { key: "six", label: "Sixes", color: "#29276B" },
  ] as const;
  const breakdown = inns.map((inn) => {
    const sum = (k: "dotBall" | "one" | "two" | "three" | "fours" | "six") => inn.batting.reduce((t, b) => t + (b[k] ?? 0), 0);
    const v = { dot: sum("dotBall"), run: sum("one") + sum("two") + sum("three"), four: sum("fours"), six: sum("six") };
    return { inn, v, n: v.dot + v.run + v.four + v.six };
  });
  const showBreakdown = breakdown.length > 0 && breakdown.every((b) => b.n > 0);

  const dots = inns
    .flatMap((inn) => inn.bowling.map((b) => ({ b, team: inn.opp })))
    .filter((r) => bowlerBalls(r.b) > 0 && r.b.dotBall != null)
    .map((r) => ({ ...r, balls: bowlerBalls(r.b), pct: (r.b.dotBall / bowlerBalls(r.b)) * 100 }))
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 6);

  if (!phases.length && !hasComm && !showBreakdown && !dots.length) {
    return (
      <section className="pane on" id="pane-analysis" role="tabpanel" aria-labelledby="tab-analysis">
        <div className="box"><Empty>{ctx.commLoading ? "Loading analysis..." : "Analysis is not available for this match."}</Empty></div>
      </section>
    );
  }

  return (
    <section className="pane on" id="pane-analysis" role="tabpanel" aria-labelledby="tab-analysis">
      {phases.length > 0 && inns.length > 0 && (
        <div className="box">
          <div className="box-h"><h3>Phase by phase</h3><span className="sub">Runs, wickets and run rate in each phase</span></div>
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Phase</th>
                  {inns.map((i) => (<Fragment key={i.id}><th>{i.team.code} runs</th><th>{i.team.code} RR</th></Fragment>))}
                  {inns.length === 2 && <th>Edge</th>}
                </tr>
              </thead>
              <tbody>
                {phases.map((p) => {
                  const c = cells(p.a, p.b);
                  const d = c[0] && c[1] ? c[1].rr - c[0].rr : null;
                  return (
                    <tr key={p.label}>
                      <td className="t">{p.label}</td>
                      {c.map((x, k) => (<Fragment key={k}><td className="big">{x ? `${x.runs}/${x.wkts}` : "–"}</td><td>{x ? x.rr.toFixed(2) : "–"}</td></Fragment>))}
                      {inns.length === 2 && (
                        <td className="s" style={{ fontWeight: 700, color: d == null ? undefined : d >= 0 ? inns[1].team.color : inns[0].team.color }}>
                          {d == null ? "–" : `${d >= 0 ? inns[1].team.code : inns[0].team.code} +${Math.abs(d).toFixed(2)}`}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasComm && (
        <div className="box">
          <div className="box-h"><h3>Runs per over</h3><span className="sub">Dots mark wickets</span></div>
          <div style={{ padding: "10px 18px 4px" }}><Manhattan inns={inns} ctx={ctx} /></div>
          <div className="legend">
            {inns.map((i) => (<span key={i.id}><i style={{ background: i.team.color, height: 10, width: 10, borderRadius: 2 }} />{i.team.name}</span>))}
            <span><i className="dot" />Wicket in the over</span>
          </div>
        </div>
      )}

      {(showBreakdown || dots.length > 0) && (
        <div className="two">
          {showBreakdown && (
            <div className="box">
              <div className="box-h"><h3>How the runs came</h3><span className="sub">Share of balls faced</span></div>
              <div className="bk">
                {breakdown.map(({ inn, v, n }) => (
                  <div key={inn.id}>
                    <div className="t"><span>{inn.team.name}</span><span className="n">{n} balls</span></div>
                    <div className="stack">
                      {cats.map((c) => (<i key={c.key} style={{ width: `${(v[c.key] / n) * 100}%`, background: c.color }} title={c.label} />))}
                    </div>
                  </div>
                ))}
                <div className="legend" style={{ padding: 0 }}>
                  {cats.map((c) => (<span key={c.key}><i style={{ background: c.color, height: 10, width: 10, borderRadius: 2 }} />{c.label}</span>))}
                </div>
              </div>
            </div>
          )}
          {dots.length > 0 && (
            <div className="box">
              <div className="box-h"><h3>Bowling control</h3><span className="sub">Dot-ball share by bowler</span></div>
              <div className="tw">
                <table>
                  <thead><tr><th>Bowler</th><th className="l">Team</th><th>Balls</th><th>Dots</th><th>Dot %</th></tr></thead>
                  <tbody>
                    {dots.map((r) => (
                      <tr key={`${r.team.name}${r.b.playerId}`}>
                        <td className="t">{personName(r.b.playerFirstName, r.b.playerLastName)}</td>
                        <td className="s l">{r.team.code}</td>
                        <td>{r.balls}</td>
                        <td>{r.b.dotBall}</td>
                        <td className="big">{Math.round(r.pct)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default AnalysisPane;
